import { EntityRecord } from '../../api/api-entities';
import { ItemRecord } from '../../api/api-items';
import { Field, Value, ConditionMode, ConditionOperator } from '../../schemas/entity';
import { FieldViewer } from '../shared/field-viewers';

function evaluateCondition(
  fieldValue: unknown,
  operator: ConditionOperator,
  expectedValue: boolean | string[]
): boolean {
  switch (operator) {
    case ConditionOperator.EQUALS:
      if (typeof expectedValue === 'boolean') {
        return fieldValue === expectedValue;
      }
      if (Array.isArray(expectedValue) && expectedValue.length > 0) {
        return fieldValue === expectedValue[0];
      }
      return false;
    case ConditionOperator.IN:
      return Array.isArray(expectedValue) && expectedValue.includes(String(fieldValue));
    default:
      return false;
  }
}

export function ItemViewer(props: { entity: EntityRecord; item: ItemRecord; className?: string }) {
  const { entity, item, className = 'space-y-4' } = props;
  const fields = entity.schema.fields ?? {};

  const shouldShowField = (_fieldKey: string, field: Field): boolean => {
    if (!field.conditions || field.conditions.groups.length === 0) {
      return true;
    }

    return field.conditions.groups.some((group) => {
      const results = group.conditions.map((condition) => {
        const fieldValue = item.attributes[condition.field];
        return evaluateCondition(fieldValue, condition.operator, condition.value);
      });

      return group.mode === ConditionMode.AND ? results.every(Boolean) : results.some(Boolean);
    });
  };

  const sortedFields = Object.entries(fields).sort(([, a], [, b]) => {
    const orderA = a.order ?? 0;
    const orderB = b.order ?? 0;
    const orderDiff = orderA - orderB;
    if (orderDiff !== 0) return orderDiff;
    return 0;
  });

  return (
    <div className={className}>
      <div className='flex flex-col'>
        {sortedFields.map(([key, field]) => {
          if (!shouldShowField(key, field)) {
            return null;
          }

          const value = item.attributes[key];
          const label = `${key}`;

          return (
            <div key={key} className='px-4 py-2'>
              <div className='flex flex-col gap-1'>
                <label className='text-sm font-medium text-neutral-950'>{label}</label>
                <FieldViewer value={value as Value} field={field} showFullContent={true} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
